import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, Col, Form, Input, Row, Select, Spin, Typography, message } from "antd";
import { MobileOutlined } from "@ant-design/icons";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { createMessageTemplate, getMessageTemplate, getMessageTemplateTypes, updateMessageTemplate } from "../../../api/Services";
import styles from "./CampaignTemplateCreate.module.scss";

const { Title, Text } = Typography;
const nameRule = /^[a-z0-9_]+$/;

const CampaignTemplateCreate: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  const getBasePath = () => {
    if (location.pathname.startsWith("/commercial-intelligence")) {
      return "/commercial-intelligence/campaigns";
    }
    return "/promotions/campaigns";
  };
  const isEditMode = !!id;
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewBody, setPreviewBody] = useState<string>("");
  const [types, setTypes] = useState<{
    statuses: any[];
    languages: any[];
    categories: any[];
  }>({ statuses: [], languages: [], categories: [] });

  const [form] = Form.useForm();

  const tr = (key: string, fallback: string) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const pickLabel = (item: any) => item?.description_es || item?.value;

  useEffect(() => {
    getMessageTemplateTypes().then(setTypes);
  }, []);

  useEffect(() => {
    if (isEditMode && id) {
      setLoading(true);
      getMessageTemplate(parseInt(id, 10))
        .then((template) => {
          if (template) {
            form.setFieldsValue({
              name: template.name,
              language: template.language,
              category: template.category,
              body: template.body,
            });
            setPreviewBody(template.body || "");
          } else {
            message.error(t("Common-ErrorLoadingData"));
            goBack();
          }
        })
        .catch(() => {
          message.error(t("Common-ErrorLoadingData"));
          goBack();
        })
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode]);

  const languageOptions = useMemo(
    () =>
      (types.languages || []).map((x: any) => ({
        label: pickLabel(x),
        value: x.value,
      })),
    [types.languages]
  );

  const categoryOptions = useMemo(
    () =>
      (types.categories || []).map((x: any) => ({
        label: pickLabel(x),
        value: x.value,
      })),
    [types.categories]
  );

  const goBack = () => navigate(`${getBasePath()}/templates`);

  const onSave = async (submitForReview: boolean) => {
    setSaving(true);
    try {
      const values = await form.validateFields();
      const ok = isEditMode && id
        ? await updateMessageTemplate(parseInt(id, 10), {
            ...values,
            submitForWhatsAppApproval: submitForReview,
          })
        : await createMessageTemplate({
            ...values,
            submitForWhatsAppApproval: submitForReview,
          });
      if (ok) {
        message.success(
          submitForReview
            ? tr("Common-SaveAndSubmitSuccess", "Plantilla creada y enviada a revisión")
            : isEditMode
            ? tr("Common-UpdateSuccessMessage", "Se ha actualizado correctamente.")
            : tr("Common-SaveDraftSuccess", "Borrador guardado correctamente")
        );
        goBack();
      } else {
        message.error(
          isEditMode ? tr("Common-EditErrorMessage", "Hubo un error al editar, intentelo más tarde") : tr("Common-CreateErrorMessage", "Error al crear")
        );
      }
    } catch (e) {
      // validation error
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "48px" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {isEditMode
              ? tr("Common-EditWhatsAppTemplate", "Editar plantilla de WhatsApp")
              : tr("Common-CreateWhatsAppTemplate", "Crear plantilla de WhatsApp")}
          </Title>
          <Text type="secondary">
            {tr(
              "Common-CreateWhatsAppTemplateHelp",
              "Cree un borrador o envíe la plantilla a revisión para aprobación."
            )}
          </Text>
        </div>
        <div className={styles.headerActions}>
          <Button onClick={goBack}>{tr("Common-Cancel", "Cancelar")}</Button>
          <Button onClick={() => onSave(false)} loading={saving}>
            {tr("Common-SaveDraft", "Guardar borrador")}
          </Button>
          <Button type="primary" onClick={() => onSave(true)} loading={saving}>
            {tr("Common-SaveAndSubmit", "Guardar y enviar a revisión")}
          </Button>
        </div>
      </div>

      <div className={styles.grid}>
        <div>
          <Form
            form={form}
            layout="vertical"
            initialValues={{ language: "ES", category: "UTILITY", body: "" }}
            onValuesChange={(_, allValues) => setPreviewBody((allValues?.body as string) || "")}
          >
            <Card title={tr("Common-GeneralInformation", "Información general")} size="small">
              <Form.Item
                name="name"
                label={tr("Common-TemplateName", "Nombre de plantilla")}
                rules={[
                  { required: true, message: tr("Common-FieldRequired", "Requerido") },
                  {
                    validator: async (_rule, value) => {
                      if (!value) return;
                      if (!nameRule.test(value)) {
                        throw new Error(
                          tr(
                            "Common-TemplateNameRule",
                            "Solo se permiten minúsculas, números y guiones bajos."
                          )
                        );
                      }
                    },
                  },
                ]}
              >
                <Input placeholder={tr("Common-TemplateNamePlaceholder", "ej. pedido_entrega")} />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="language"
                    label={tr("Common-Language", "Idioma")}
                    rules={[{ required: true, message: tr("Common-FieldRequired", "Requerido") }]}
                  >
                    <Select options={languageOptions} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="category"
                    label={tr("Common-Category", "Categoría")}
                    rules={[{ required: true, message: tr("Common-FieldRequired", "Requerido") }]}
                  >
                    <Select options={categoryOptions} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card
              title={tr("Common-Content", "Contenido")}
              size="small"
              style={{ marginTop: 16 }}
            >
              <Form.Item
                name="body"
                label={tr("Common-MessageBody", "Mensaje")}
                rules={[{ required: true, message: tr("Common-FieldRequired", "Requerido") }]}
              >
                <Input.TextArea
                  rows={6}
                  placeholder={tr("Common-TemplateBodyPlaceholder", "Escriba el contenido del mensaje")}
                />
              </Form.Item>
            </Card>
          </Form>
        </div>

        <Card title={tr("Common-Preview", "Vista previa")} size="small">
          <div className={styles.previewPhone}>
            <div className={styles.previewScreen}>
              <div className={styles.whatsappHeader}>
                <MobileOutlined className={styles.whatsappHeaderIcon} />
                <span>{tr("Common-YourPhoneNumber", "Tu número de teléfono")}</span>
              </div>
              <div className={styles.chatArea}>
                <div className={styles.bubble}>
                  {previewBody && previewBody.trim().length > 0
                    ? previewBody
                    : tr("Common-PreviewEmpty", "La vista previa aparecerá aquí.")}
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CampaignTemplateCreate;

